import React, { useCallback, useEffect, useState, useRef } from 'react'; // 🟢 CORREZIONE: Aggiunto useRef qui
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button'; 
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import MainCard from '@/components/MainCard';
import { useDashboardFilters } from '../../contexts/DashboardFilterContext';
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import axiosInstance from "@/utils/axios";
import { useIntl } from "react-intl";
import { circularIconHoverSx } from "@/styles/iconHover";

// ─── Tipi ───────────────────────────────────────────────────────────────────

interface FilterOptions {
  contentType: string[];
  season: string[];
  competition: string[];
}

interface BackendCompetition {
  id: string;
  name: string;
  type: string;
  season: string;
  external_id: number;
  knockout: boolean | null;
  number_competitors: number | null;
  matchdays: number;
  thumbnail: string;
  match_types: string[];
  round_types: any[];
}

interface ProcessedCompetitions {
  competition: string[];
  season: string[];
}

// ─── Fetch API ──────────────────────────────────────────────────────────────

async function fetchContentType(): Promise<string[]> {
  const res = await axiosInstance.get('/dashboard/getContentType');
  return res.data;
}

async function fetchCompetitions(): Promise<ProcessedCompetitions> {
  const res = await axiosInstance.get('/competitions/getCompetitions');
  const rawList: BackendCompetition[] = res.data;

  const uniqueCompetitionNames = Array.from(
    new Set(rawList.map((item) => item.name).filter(Boolean))
  );
  
  const uniqueSeasonYears = Array.from(
    new Set(rawList.map((item) => item.season).filter(Boolean))
  );

  const competitionOptions = [
    'All', 
    ...uniqueCompetitionNames.filter(name => name !== 'All').sort((a, b) => a.localeCompare(b))
  ];
  
  const seasonOptions = [
    'All', 
    ...uniqueSeasonYears.filter(year => year !== 'All').sort((a, b) => Number(a) - Number(b))
  ];

  return {
    competition: competitionOptions,
    season: seasonOptions
  };
}

// ─── Helper di Selezione Intelligente ────────────────────────────────────────

function resolveMultiSelect(
  newValue: string[],
  currentValues: string[],
  options: string[]
): string[] {
  const realOptions = options.filter((o) => o !== 'All');
  const hadAll = currentValues.includes('All');
  const hasAll = newValue.includes('All');

  if (hasAll && !hadAll) return ['All', ...realOptions];
  if (!hasAll && hadAll) return [];

  const selectedWithoutAll = newValue.filter((v) => v !== 'All');

  if (hadAll && selectedWithoutAll.length < realOptions.length) {
    return selectedWithoutAll;
  }

  if (selectedWithoutAll.length === realOptions.length) {
    return ['All', ...realOptions];
  }

  return selectedWithoutAll;
}

// ─── Sub-componente Filtro Multiplo ──────────────────────────────────────────

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  hasAll?: boolean; 
}

function MultiSelectFilter({ label, options, value, onChange, hasAll = true }: MultiSelectFilterProps) {
  const intl = useIntl();
  
  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: string[]) => {
      if (hasAll) {
        onChange(resolveMultiSelect(newValue, value, options));
      } else {
        onChange(newValue);
      }
    },
    [value, onChange, options, hasAll]
  );

  return (
    <Autocomplete
      multiple
      fullWidth
      limitTags={1}
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={handleChange}
      getOptionLabel={(option) => intl.formatMessage({ id: option, defaultMessage: option })}
      sx={{
        '& .MuiInputBase-root': {
          height: '53.13px !important',
          paddingTop: '0px !important',
          paddingBottom: '0px !important',
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiInputLabel-root': { top: '3px' },
        '& .MuiInputLabel-shrink': { top: '0px' },
      }}
      renderTags={(selected) => {
        const maxVisible = 1;
        const cleanSelected = selected.filter((option) => option !== 'All');

        return (
          <div key={`div_Chip_${label}`} style={{ display: 'flex', alignItems: 'center' }}>
            {cleanSelected.slice(0, maxVisible).map((option, index) => {
              if (!option) return null;
              const translated = intl.formatMessage({ id: option, defaultMessage: option });
              const cleanLabel = translated.replaceAll('_', ' ');
              return (
                <Chip
                  key={`chip-${label}_${index}`}
                  size="small"
                  label={cleanLabel.length > 15 ? `${cleanLabel.slice(0, 15)}...` : cleanLabel}
                />
              );
            })}
            
            {cleanSelected.length > maxVisible && (
              <Tooltip
                key={`Tooltip-chip-extra_${label}`}
                title={cleanSelected.slice(maxVisible).map((item, index) => {
                  const translatedItem = intl.formatMessage({ id: item, defaultMessage: item });
                  return <div key={index}>{translatedItem.replaceAll('_', ' ')}</div>;
                })}
              >
                <Chip
                  key={`chip-extra_${label}`}
                  size="small"
                  label={`${cleanSelected.length - maxVisible}+`}
                  style={{ marginLeft: '5px' }}
                />
              </Tooltip>
            )}
          </div>
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        const translatedOption = intl.formatMessage({ id: option, defaultMessage: option });
        return (
          <li key={key} {...rest}>
            <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
            {translatedOption.replaceAll('_', ' ')}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value && value.length > 0 ? '' : 'Search...'}
        />
      )}
    />
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function DashboardFilters() {
  const intl = useIntl(); 
  
  const {
    contentType, setContentType,
    competition, setCompetition,
    season, setSeason,
    dateRange, setDateRange,
    updateDashboard, setUpdateDashboard,
    setFilterLoaded
  } = useDashboardFilters();

  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🟢 CORREZIONE 1: Inizializzazione del Ref in memoria per gestire lo stato del debounce anti-spam
  const isDebouncingRef = useRef(false);

  useEffect(() => {
    
    setUpdateDashboard(0)
    let cancelled = false;

    if (!contentType || contentType.length === 0) setContentType(['All']);
    if (!competition || competition.length === 0) setCompetition(['All']);
    if (!season || season.length === 0) setSeason(['All']);

    const fetchAll = async () => {
      try {
        setFilterLoaded(false)
        setIsLoading(true);
        setError(null);

        const [rawContentType, processedCompetitions] = await Promise.all([
          fetchContentType(),
          fetchCompetitions(),
        ]);

        const contentTypeOptions = [
          'All',
          ...rawContentType.filter(o => o !== 'All').sort((a, b) => a.localeCompare(b))
        ];
        
        const { competition: competitionOptions, season: seasonOptions } = processedCompetitions;

        if (!cancelled) {
          setFilterOptions({
            contentType: contentTypeOptions,
            competition: competitionOptions,
            season: seasonOptions,
          });

          if (!contentType || contentType.length === 0 || contentType.includes('All')) {
            setContentType(['All', ...contentTypeOptions.filter(o => o !== 'All')]);
          }
          if (!competition || competition.includes('All')) {
            setCompetition(['All', ...competitionOptions.filter(o => o !== 'All')]);
          }
          if (!season || season.includes('All')) {
            setSeason(['All', ...seasonOptions.filter(o => o !== 'All')]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load filters options');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);          
        }
        setFilterLoaded(true)
        setUpdateDashboard(1)
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const handleApplyFilters = useCallback(() => {
    if (isDebouncingRef.current || !updateDashboard) return;
    isDebouncingRef.current = true;

    if (typeof setUpdateDashboard === 'function') {
      const updateDashboard = setUpdateDashboard as React.Dispatch<React.SetStateAction<number>>;
      updateDashboard((prev) => (typeof prev === 'number' ? prev + 1 : 1));
    }

    setTimeout(() => {
      isDebouncingRef.current = false;
    }, 1000);
    
  }, [updateDashboard]);

  if (isLoading) {
    return (
      <MainCard >
        <Grid container justifyContent="center" alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={28} />
        </Grid>
      </MainCard>
    );
  }

  if (error || !filterOptions) {
    return (
      <MainCard title="Dashboard Filters">
        <Grid container justifyContent="center" sx={{ py: 2, color: 'error.main' }}>
          {error ?? 'Opzioni non disponibili'}
        </Grid>
      </MainCard>
    );
  }

  return (
    <MainCard title="Dashboard Filters" >
      <Grid container spacing={2} alignItems="center" sx={{ px: 2 }}>
        
        {/* Sezione Filtri Superiori */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MultiSelectFilter
            label="Content Type"
            options={filterOptions.contentType}
            value={contentType ?? []}
            onChange={setContentType}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MultiSelectFilter
            label="Competition"
            options={filterOptions.competition}
            value={competition ?? []}
            onChange={setCompetition}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MultiSelectFilter
            label="Season"
            options={filterOptions.season}
            value={season ?? []}
            onChange={setSeason}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }} sx={{ ml: { lg: 'auto' } }}>
          <DateRangePicker
            format="DD/MM/YYYY"
            localeText={{ start: 'Start Date', end: 'End Date' }}
            value={dateRange}
            onChange={setDateRange}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: {
                  '& .MuiInputBase-root': { height: '50px' },
                  '& .MuiInputLabel-root': { top: '-2px' },
                  '& .MuiInputLabel-shrink': { top: '0px' },
                },
              },
              openPickerButton: {
                sx: circularIconHoverSx(),
              },
            }}
          />
        </Grid>

        {/* Pulsante di aggiornamento esplicito */}
        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilters}
            sx={{ minWidth: '160px', height: '42px', fontWeight: 600 }}
          >
            {intl.formatMessage({ id: 'update_dashboard', defaultMessage: 'Update Dashboard' })}
          </Button>
        </Grid>

      </Grid>
    </MainCard>
  );
}